import ConfirmModal from '@/components/confirm-modal'
import {
    AgendaBlock,
    deleteAgendaBlock,
} from '@/lib/api/agenda-blocks/agenda-blocks-api'
import { useState } from 'react'
import { MdAdd, MdBlock, MdExpandLess, MdExpandMore } from 'react-icons/md'
import { toast } from 'react-toastify'
import { BlockCard } from './BlockCard'
import { BlockModal } from './BlockModal'

interface BlockPanelProps {
    agendaId: number
    blocks: AgendaBlock[]
    onRefresh: () => void
    collapsible?: boolean
    defaultCollapsed?: boolean
}

export function BlockPanel({
    agendaId,
    blocks,
    onRefresh,
    collapsible = true,
    defaultCollapsed = false,
}: BlockPanelProps) {
    const [collapsed, setCollapsed] = useState(defaultCollapsed)
    const [showModal, setShowModal] = useState(false)
    const [selectedBlock, setSelectedBlock] = useState<AgendaBlock | null>(null)
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [blockToDelete, setBlockToDelete] = useState<AgendaBlock | null>(null)
    const [deleting, setDeleting] = useState(false)

    const handleAdd = () => {
        setSelectedBlock(null)
        setShowModal(true)
    }

    const handleEdit = (block: AgendaBlock) => {
        setSelectedBlock(block)
        setShowModal(true)
    }

    const handleDelete = (block: AgendaBlock) => {
        setBlockToDelete(block)
        setShowDeleteModal(true)
    }

    const confirmDelete = async () => {
        if (!blockToDelete) return

        setDeleting(true)
        try {
            const response = await deleteAgendaBlock(blockToDelete.id)
            if (response.success) {
                toast.success('Bloqueio removido com sucesso')
                setShowDeleteModal(false)
                setBlockToDelete(null)
                onRefresh()
            } else {
                toast.error(response.error || 'Erro ao remover bloqueio')
            }
        } catch (error) {
            toast.error('Erro ao remover bloqueio')
        } finally {
            setDeleting(false)
        }
    }

    const handleCloseModal = () => {
        setShowModal(false)
        setSelectedBlock(null)
    }

    const handleSuccess = () => {
        onRefresh()
    }

    return (
        <div className="bg-base-100 rounded-xl border border-base-300/50">
            <div
                className={`flex items-center justify-between p-4 ${collapsible ? 'cursor-pointer' : ''
                    }`}
                onClick={() => collapsible && setCollapsed(!collapsed)}
            >
                <div className="flex items-center gap-2">
                    <MdBlock className="text-xl text-base-content/70" />
                    <h3 className="font-semibold text-base-content">
                        Bloqueios
                    </h3>
                    {blocks.length > 0 && (
                        <span className="badge badge-sm badge-ghost">
                            {blocks.length}
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    <button
                        className="btn btn-sm btn-ghost btn-square"
                        onClick={e => {
                            e.stopPropagation()
                            handleAdd()
                        }}
                        title="Adicionar bloqueio"
                    >
                        <MdAdd className="text-lg" />
                    </button>
                    {collapsible && (
                        <button className="btn btn-sm btn-ghost btn-square">
                            {collapsed ? (
                                <MdExpandMore className="text-lg" />
                            ) : (
                                <MdExpandLess className="text-lg" />
                            )}
                        </button>
                    )}
                </div>
            </div>

            {!collapsed && (
                <div className="px-4 pb-4 space-y-3 max-h-[400px] overflow-y-auto">
                    {blocks.length === 0 ? (
                        <div className="text-center py-6">
                            <MdBlock className="text-4xl text-base-content/30 mx-auto mb-2" />
                            <p className="text-sm text-base-content/60">
                                Nenhum bloqueio configurado
                            </p>
                            <button
                                className="btn btn-sm btn-secondary mt-3"
                                onClick={handleAdd}
                            >
                                <MdAdd className="text-lg" />
                                Adicionar bloqueio
                            </button>
                        </div>
                    ) : (
                        blocks.map(block => (
                            <BlockCard
                                key={block.id}
                                block={block}
                                onEdit={handleEdit}
                                onDelete={handleDelete}
                            />
                        ))
                    )}
                </div>
            )}

            {showModal && (
                <BlockModal
                    agendaId={agendaId}
                    block={selectedBlock}
                    onClose={handleCloseModal}
                    onSuccess={handleSuccess}
                />
            )}

            <ConfirmModal
                isOpen={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false)
                    setBlockToDelete(null)
                }}
                onConfirm={confirmDelete}
                title="Excluir bloqueio"
                message={`Tem certeza de que deseja excluir o bloqueio "${blockToDelete?.title}"? Esta ação não pode ser desfeita.`}
                confirmButtonText="Excluir"
                cancelButtonText="Cancelar"
                loading={deleting}
            />
        </div>
    )
}
